'use client';
import {
  experimentInstance,
  startExperiment,
  type GlobalWalletVariant,
  type GlobalWalletVariantPayload,
} from '@payments-checkout/shared/plugins/analitica/analitica.configs';
import {
  usePaymentMethod,
  useSessionId,
} from '@payments-checkout/shared/providers';
import { useParams } from 'next/navigation';
import { useEffect, useRef } from 'react';
import { usePaymentLinkId } from '../usePaymentLinkId';

const EXPERIMENT_KEY = 'vendas_core_link_checkout_carteira_global';
const VARIANT_TIMEOUT_MS = 1500;

const ENROLLED_VARIANT_KEYS: Exclude<GlobalWalletVariant, 'pending'>[] = [
  'switch_on_a',
  'switch_off_a',
  'switch_on_b',
  'switch_off_b',
];

function isEnrolledVariantKey(
  key: unknown
): key is Exclude<GlobalWalletVariant, 'pending'> {
  return ENROLLED_VARIANT_KEYS.includes(key as never);
}

function isValidPayload(payload: unknown): payload is GlobalWalletVariantPayload {
  const candidate = payload as Partial<GlobalWalletVariantPayload> | undefined;
  return (
    typeof candidate?.title === 'string' &&
    typeof candidate?.description === 'string' &&
    typeof candidate?.tooltip_text === 'string'
  );
}

function withTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number
): Promise<T | undefined> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(undefined), timeoutMs);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      () => {
        clearTimeout(timer);
        resolve(undefined);
      }
    );
  });
}

export function useLoadGlobalWalletExperiment() {
  const params = useParams<{ paymentLinkId: string }>();
  const { data } = usePaymentLinkId(params?.paymentLinkId);
  const { sessionId } = useSessionId();
  const {
    setGlobalWalletVariant,
    setGlobalWalletVariantPayload,
    setSaveCardForFutureChecked,
  } = usePaymentMethod();
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;

    const merchantId = data?.account_settings?.merchant_id;
    const amplitudeIdentifier = sessionStorage.getItem('amplitudeIdentifier');
    if (!merchantId || !amplitudeIdentifier) return;

    startedRef.current = true;

    (async () => {
      try {
        await startExperiment({
          userId: amplitudeIdentifier,
          accountId: data?.account_settings?.account_id,
          merchantId,
        });

        const variant = await withTimeout(
          experimentInstance.variant(EXPERIMENT_KEY),
          VARIANT_TIMEOUT_MS
        );

        if (
          !variant ||
          !isEnrolledVariantKey(variant.key) ||
          !isValidPayload(variant.payload)
        ) {
          return;
        }

        setGlobalWalletVariant(variant.key);
        setGlobalWalletVariantPayload(variant.payload);
        setSaveCardForFutureChecked(variant.key.startsWith('switch_on'));
      } catch {
        // Falha/timeout: mantém `pending`.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sessionId,
    data?.account_settings?.merchant_id,
    setGlobalWalletVariant,
    setGlobalWalletVariantPayload,
    setSaveCardForFutureChecked,
  ]);
}
